import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DataGrid } from '../components/DataGrid';
import { toast } from 'sonner';
import { PencilSimple, SignOut, Plus } from '@phosphor-icons/react';

const MANAGE_ROLES = ['global_admin', 'school_admin', 'principal', 'registrar'];
const CREATE_ROLES = [...MANAGE_ROLES, 'admissions_officer'];
const STATUS_TONE = { active: 'bg-green-100 text-green-800', exited: 'bg-slate-100 text-slate-500', erased: 'bg-rose-100 text-rose-700' };

export default function Students() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: students, isLoading } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const visibleStudents = students || [];
  const { data: meta } = useQuery({ queryKey: ['meta-grades'], queryFn: () => api.get('/meta/grades').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: CREATE_ROLES.includes(user?.role) });
  const school = (tenants || []).find((t) => t.type === 'school');
  const grades = meta?.grades || [];
  const sections = meta?.sections || [];
  const canManage = MANAGE_ROLES.includes(user?.role);
  const canCreate = CREATE_ROLES.includes(user?.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', grade: '1', section: 'A', gender: '', dob: '' });
  const [editS, setEditS] = useState(null);

  const inval = () => qc.invalidateQueries({ queryKey: ['students'] });
  const createMut = useMutation({
    mutationFn: () => api.post('/students', { ...form, tenant_id: user?.tenant_id || school?.id }),
    onSuccess: (r) => { toast.success(`Student created - ${r.data.admission_no}`); setCreateOpen(false); setForm({ name: '', grade: '1', section: 'A', gender: '', dob: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const updateMut = useMutation({
    mutationFn: () => api.patch(`/students/${editS.id}`, { name: editS.name, grade: editS.grade, section: editS.section, reason: editS.reason || null }),
    onSuccess: (r) => { toast.success(r.data.moved ? 'Student transferred - lifecycle mover event recorded' : 'Student updated'); setEditS(null); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const exitMut = useMutation({
    mutationFn: ({ id, name, reason }) => api.post('/lifecycle/transition', { subject_type: 'student', subject_id: id, event: 'leaver', detail: reason || 'Exited via Students module' }),
    onSuccess: () => { toast.success('Student exited - exit checklist created in Lifecycle'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="students-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Students</h1>
        {canCreate && (
          <Button data-testid="new-student-button" onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1" /> New student</Button>
        )}
      </div>
      <DataGrid gridId="students" rows={visibleStudents} loading={isLoading} exportName="students"
        columns={[
          { key: 'admission_no', label: 'Admission #', render: (s) => <span className="font-mono-label text-xs">{s.admission_no}</span> },
          { key: 'name', label: 'Name', render: (s) => <Link data-testid={`student-link-${s.id}`} to={`/students/${s.id}`} className="font-medium text-slate-900 hover:underline">{s.name}</Link> },
          { key: 'grade', label: 'Grade' },
          { key: 'section', label: 'Section' },
          { key: 'status', label: 'Status', render: (s) => <span className={`text-xs rounded px-2 py-0.5 ${STATUS_TONE[s.status] || 'bg-slate-100'}`}>{s.status}</span> },
          { key: 'gender', label: 'Gender', hidden: true },
          { key: 'dob', label: 'Date of birth', hidden: true },
          ...(canManage ? [{ key: '_actions', label: 'Actions', render: (s) => s.status === 'active' && (
            <div className="flex gap-1.5">
              <button data-testid={`student-edit-${s.id}`} title="Edit / transfer grade-section" onClick={(e) => { e.stopPropagation(); setEditS({ ...s, reason: '' }); }}
                className="text-slate-400 hover:text-slate-900 p-1"><PencilSimple size={15} /></button>
              <button data-testid={`student-exit-${s.id}`} title="Exit / terminate (lifecycle leaver)"
                onClick={(e) => { e.stopPropagation(); const r = window.prompt(`Exit ${s.name}? This revokes access and starts the exit checklist. Reason:`); if (r !== null) exitMut.mutate({ id: s.id, name: s.name, reason: r }); }}
                className="text-slate-400 hover:text-rose-600 p-1"><SignOut size={15} /></button>
            </div>) }] : []),
        ]}
        filters={[
          { key: 'grade', label: 'Grade', options: [...new Set((students || []).map((s) => s.grade))] },
          { key: 'status', label: 'Status', options: ['active', 'alumni', 'exited'] },
        ]}
        emptyText="No students visible in your scope." />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="student-create-dialog">
          <DialogHeader><DialogTitle className="font-display">New student</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="student-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="flex gap-2">
              <select data-testid="student-grade-select" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
                className="flex-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {grades.map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
              </select>
              <select data-testid="student-section-select" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}
                className="w-32 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {sections.map((s) => <option key={s} value={s}>Section {s}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Input data-testid="student-dob-input" type="date" className="flex-1" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              <select data-testid="student-gender-select" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-32 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                <option value="">Gender...</option>
                {['female', 'male', 'other'].map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <Button data-testid="student-create-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!form.name || createMut.isPending} onClick={() => createMut.mutate()}>Create student</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editS} onOpenChange={() => setEditS(null)}>
        <DialogContent data-testid="student-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit / transfer student</DialogTitle></DialogHeader>
          {editS && (
            <div className="space-y-3">
              <Input data-testid="edit-student-name" value={editS.name} onChange={(e) => setEditS({ ...editS, name: e.target.value })} />
              <div className="flex gap-2">
                <select data-testid="edit-student-grade" value={editS.grade} onChange={(e) => setEditS({ ...editS, grade: e.target.value })}
                  className="flex-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {grades.map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
                </select>
                <select data-testid="edit-student-section" value={editS.section} onChange={(e) => setEditS({ ...editS, section: e.target.value })}
                  className="w-32 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {sections.map((s) => <option key={s} value={s}>Section {s}</option>)}
                </select>
              </div>
              <Input data-testid="edit-student-reason" placeholder="Reason (recorded on transfer)" value={editS.reason} onChange={(e) => setEditS({ ...editS, reason: e.target.value })} />
              <p className="text-[11px] text-slate-400">Changing grade or section records a lifecycle "mover" (transfer) event automatically.</p>
              <Button data-testid="edit-student-save" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!editS.name || updateMut.isPending} onClick={() => updateMut.mutate()}>Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
